import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

const SettingUpDigitalBusinessCard = () => {
  useSEO({
    title: "How to Set Up Your Digital Business Card | AM CARDMATE Blog",
    description: "Step-by-step guide to creating and customizing your perfect digital business card with AM CARDMATE's platform.",
    image: "/assets/setnfc.jpg",
    keywords: "setup digital business card, create digital card, business card guide, getting started"
  });

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": "How to Set Up Your Digital Business Card",
        "description": "Step-by-step guide to creating your perfect digital business card",
        "datePublished": "2025-05-28",
        "dateModified": "2025-05-28",
        "author": { "@type": "Organization", "name": "AM CARDMATE Support Team" },
        "publisher": { "@type": "Organization", "name": "AM CARDMATE", "logo": { "@type": "ImageObject", "url": "https://amcardmate.com/assets/logo.svg" } },
        "image": "https://amcardmate.com/assets/setnfc.jpg",
        "mainEntityOfPage": { "@type": "WebPage", "@id": "https://amcardmate.com/blog/setting-up-digital-business-card" }
      },
      {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "Setting Up Your First Digital Business Card",
        "estimatedTime": "PT10M",
        "step": [
          { "@type": "HowToStep", "name": "Download the AM CARDMATE App", "text": "Get our free app from App Store or Google Play" },
          { "@type": "HowToStep", "name": "Create Your Account", "text": "Sign up with email or social login" },
          { "@type": "HowToStep", "name": "Build Your Profile", "text": "Add your photo, contact info, and social links" },
          { "@type": "HowToStep", "name": "Customize Your Design", "text": "Choose colors and themes that match your brand" },
          { "@type": "HowToStep", "name": "Preview and Test", "text": "Check everything looks perfect" },
          { "@type": "HowToStep", "name": "Go Live & Start Sharing", "text": "Share via link, QR code, or NFC card" }
        ]
      }
    ];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  const [currentStep, setCurrentStep] = useState(1);

  const steps = [
    {
      num: 1, title: 'Download the AM CARDMATE App', gradient: 'from-blue-50 to-purple-50', numGradient: 'from-blue-400 to-blue-600',
      content: "First things first – grab our app. It's free, it's beautiful, and it's about to become your new best friend in networking.",
      tipColor: 'bg-blue-100', tip: "Allow notifications during setup. We'll send you tips and let you know when someone views your card!",
      extra: (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <a href="https://apps.apple.com/pt/app/am-cardmate/id6747951046" target="_blank" rel="noopener noreferrer" className="bg-black text-white rounded-xl p-4 flex items-center gap-4 hover:bg-gray-900 transition-colors">
            <FontAwesomeIcon icon={['fab', 'apple']} className="text-3xl" />
            <div><div className="text-caption opacity-80">Download on the</div><div className="text-btn font-bold">App Store</div></div>
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.amcardmate" target="_blank" rel="noopener noreferrer" className="bg-black text-white rounded-xl p-4 flex items-center gap-4 hover:bg-gray-900 transition-colors">
            <FontAwesomeIcon icon={['fab', 'google-play']} className="text-3xl" />
            <div><div className="text-caption opacity-80">Get it on</div><div className="text-btn font-bold">Google Play</div></div>
          </a>
        </div>
      )
    },
    {
      num: 2, title: 'Create Your Account', gradient: 'from-purple-50 to-pink-50', numGradient: 'from-purple-400 to-purple-600',
      content: "Time to make it official! Creating your account is quick and painless – we promise no 47-field forms here.",
      tipColor: 'bg-purple-100', tip: "We use bank-level encryption to protect your data. Your information is safer than a secret recipe in a vault!",
      extra: (
        <div className="space-y-3 mb-6">
          {[{ title: 'Email Sign-Up', desc: "Use your professional email – this is what you'll log in with." }, { title: 'Social Sign-In', desc: 'Or use Google/Apple sign-in for even faster setup!' }, { title: 'Choose Your Plan', desc: 'Start with our free plan – you can always upgrade later!' }].map((opt, i) => (
            <div key={i} className="bg-white rounded-xl p-4 text-center">
              <h4 className="text-card-title text-dark mb-1">{opt.title}</h4>
              <p className="text-body-sm text-gray-600">{opt.desc}</p>
            </div>
          ))}
        </div>
      )
    },
    {
      num: 3, title: 'Build Your Profile', gradient: 'from-green-50 to-teal-50', numGradient: 'from-green-400 to-green-600',
      content: "This is where the magic happens! Let's create a profile that makes people want to connect with you.",
      tipColor: 'bg-green-100', tip: "Add a compelling bio (2-3 sentences), include a call-to-action button, and upload your company logo for brand consistency.",
      extra: (
        <div className="grid grid-cols-2 gap-4 mb-6">
          {[{ icon: 'camera', title: 'Profile Photo', desc: "Use a professional headshot. Smile – you're going digital!" }, { icon: 'user', title: 'Basic Info', desc: 'Name, title, company – the essentials that matter.' }, { icon: 'phone', title: 'Contact Details', desc: 'Phone, email, website – how people reach you.' }, { icon: 'share-alt', title: 'Social Links', desc: 'LinkedIn, Twitter, Instagram – your digital presence.' }].map((item, i) => (
            <div key={i} className="bg-white rounded-xl p-4 text-center">
              <FontAwesomeIcon icon={item.icon} className="text-2xl text-green-500 mb-2" />
              <h4 className="text-card-title text-dark">{item.title}</h4>
              <p className="text-body-sm text-gray-600">{item.desc}</p>
            </div>
          ))}
        </div>
      )
    },
    {
      num: 4, title: 'Customize Your Design', gradient: 'from-pink-50 to-yellow-50', numGradient: 'from-pink-400 to-pink-600',
      content: "Time to make it yours! Choose colors, themes, and layouts that match your personal brand.",
      tipColor: 'bg-pink-100', tip: "Match your card design to your industry. Tech? Go modern. Finance? Keep it professional. Creative? Let your personality shine!",
      extra: (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[{ gradient: 'from-blue-400 to-blue-600', label: 'Professional' }, { gradient: 'from-purple-400 to-pink-600', label: 'Creative' }, { gradient: 'from-green-400 to-teal-600', label: 'Modern' }, { gradient: 'from-gray-600 to-gray-800', label: 'Minimal' }].map((theme, i) => (
            <div key={i} className="bg-white rounded-xl p-4 text-center">
              <div className={`w-12 h-12 bg-gradient-to-br ${theme.gradient} rounded-lg mx-auto mb-2`}></div>
              <p className="text-body-sm">{theme.label}</p>
            </div>
          ))}
        </div>
      )
    },
    {
      num: 5, title: 'Preview and Test', gradient: 'from-blue-50 to-indigo-50', numGradient: 'from-blue-500 to-indigo-600',
      content: "Before you go live, let's make sure everything looks perfect. This is your chance to catch any typos or adjust your design!",
      tipColor: 'bg-blue-100', tip: "Send your profile link to a friend or colleague for feedback. Fresh eyes catch things you might miss!",
      extra: (
        <div className="bg-white rounded-xl p-6 mb-6">
          <h4 className="text-card-title text-dark mb-4">Preview Checklist:</h4>
          <div className="space-y-2">
            {['Profile photo looks professional', 'All contact info is correct', 'Social links work properly', 'Bio clearly explains what you do', 'Design matches your brand'].map((item, i) => (
              <label key={i} className="flex items-center gap-3">
                <input type="checkbox" className="w-5 h-5" />
                <span className="text-body text-gray-600">{item}</span>
              </label>
            ))}
          </div>
        </div>
      )
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 md:pt-36 lg:pt-40 pb-20 bg-gradient-to-b from-white via-blue-50/20 to-white relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            <nav className="flex flex-wrap items-center gap-2 text-caption mb-6">
              <Link to="/" className="hover:text-primary-blue transition-colors">Home</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <Link to="/blog" className="hover:text-primary-blue transition-colors">Blog</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <span className="text-primary-blue">Setting Up Your Digital Card</span>
            </nav>

            <div className="text-center mb-8">
              <div className="badge badge-blue inline-flex mb-6">
                <FontAwesomeIcon icon="rocket" />
                <span>Getting Started</span>
              </div>

              <h1 className="text-h1 mb-6">
                Setting Up Your First
                <span className="block text-gradient-blue-pink mt-2">Digital Business Card</span>
              </h1>

              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-caption">
                <span className="flex items-center gap-2"><FontAwesomeIcon icon="user" className="text-primary-blue" /> Support Team</span>
                <span className="flex items-center gap-2"><FontAwesomeIcon icon="calendar" className="text-primary-blue" /> May 28, 2025</span>
                <span className="flex items-center gap-2"><FontAwesomeIcon icon="clock" className="text-primary-blue" /> 8 min read</span>
              </div>
            </div>

            <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-12">
              <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop" alt="Digital business card setup" className="w-full h-auto" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <p className="text-body-lg text-gray-600 mb-8">
              Ready to join the digital revolution? Perfect! Setting up your AM CARDMATE digital business card is easier than making instant coffee, and way more satisfying. In just 10 minutes, you'll have a professional digital presence that'll make paper cards jealous. Let's dive in!
            </p>

            <div className="bg-gradient-to-r from-primary-blue/10 to-accent-purple/10 rounded-2xl p-6 md:p-8 mb-12">
              <h3 className="text-h4 text-dark mb-4 flex items-center gap-3">
                <FontAwesomeIcon icon="clock" className="text-primary-blue" />
                Time Investment
              </h3>
              <p className="text-body text-gray-600">Initial setup: 10 minutes. Looking awesome forever: Priceless. Seriously, that's all it takes to create a digital business card that updates itself and never runs out!</p>
            </div>

            {/* Progress Bar */}
            <div className="mb-12">
              <div className="bg-gray-100 rounded-full h-3 mb-4">
                <div className="bg-gradient-to-r from-primary-blue to-accent-purple h-3 rounded-full transition-all duration-500" style={{ width: `${(currentStep / 6) * 100}%` }} />
              </div>
              <div className="flex justify-between text-caption">
                <span>Start</span>
                <span className="text-primary-blue font-bold">Step {currentStep} of 6</span>
                <span>Complete!</span>
              </div>
            </div>

            {/* Steps 1-5 */}
            <div className="space-y-8 mb-12">
              {steps.map((step) => (
                <div key={step.num} className={`bg-gradient-to-r ${step.gradient} rounded-3xl p-6 md:p-8 shadow-lg`}>
                  <div className="flex flex-col sm:flex-row items-start gap-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${step.numGradient} rounded-xl flex items-center justify-center text-white flex-shrink-0`}>
                      <span className="text-h5 font-bold">{step.num}</span>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-h4 text-dark mb-4">{step.title}</h2>
                      <p className="text-body text-gray-600 mb-6">{step.content}</p>
                      {step.extra}
                      <div className={`${step.tipColor} rounded-xl p-4`}>
                        <p className="text-body-sm text-gray-700"><strong>Pro tip:</strong> {step.tip}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Step 6 - CTA Step */}
            <div className="mb-16">
              <div className="bg-gradient-to-r from-[#4DA2FF] to-[#DE6161] text-white rounded-3xl p-6 md:p-8 shadow-xl relative overflow-hidden">
                <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
                <div className="relative z-10">
                  <div className="flex flex-col sm:flex-row items-start gap-4">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-h5 font-bold">6</span>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-h4 text-white mb-4">Go Live & Start Sharing!</h2>
                      <p className="text-body text-white/90 mb-6">Congratulations! Your digital business card is ready. Now let's get it out into the world!</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                        {[{ title: 'Share via Link', desc: 'Copy your unique URL and share it anywhere – email signatures, social media, texts!' }, { title: 'QR Code', desc: 'Download your QR code for printed materials or virtual backgrounds.' }, { title: 'NFC Card', desc: 'Order your physical NFC card for that magical tap-to-share experience!' }, { title: 'Email Widget', desc: 'Add a clickable card preview to your email signature.' }].map((item, i) => (
                          <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                            <h4 className="font-bold mb-1 text-white">{item.title}</h4>
                            <p className="text-body-sm text-white/80">{item.desc}</p>
                          </div>
                        ))}
                      </div>
                      <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center mt-8">
                        <p className="text-body font-bold text-white mb-1">🎉 You did it!</p>
                        <p className="text-body-sm text-white/90">Your digital business card is live and ready to impress. Welcome to the future of networking!</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pro Tips */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-pink-500 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="lightbulb" />
                </div>
                <h2 className="text-h3 text-dark">Pro Tips for Digital Card Success</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { bg: 'from-primary-blue/10 to-accent-purple/10', title: 'Keep It Updated', desc: "Changed jobs? New phone number? Update your card instantly. That's the beauty of digital – no reprinting needed!" },
                  { bg: 'from-pink-50 to-orange-50', title: 'Track Your Analytics', desc: "Check your dashboard weekly to see who's viewing your card. Follow up with interested connections while you're fresh in their mind!" },
                  { bg: 'from-green-50 to-teal-50', title: 'Use Multiple Profiles', desc: 'Create different cards for different contexts – networking events, client meetings, conferences. Tailor your message!' },
                  { bg: 'from-purple-50 to-blue-50', title: 'Share Strategically', desc: 'Add your card link to email signatures, LinkedIn profile, and social media bios. Make it easy for people to find you!' }
                ].map((tip, i) => (
                  <div key={i} className={`bg-gradient-to-br ${tip.bg} rounded-2xl p-6`}>
                    <h3 className="text-card-title text-dark mb-3">{tip.title}</h3>
                    <p className="text-card-body">{tip.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Common Mistakes */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-red-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="exclamation-triangle" />
                </div>
                <h2 className="text-h3 text-dark">Common Mistakes to Avoid</h2>
              </div>
              <div className="space-y-4">
                {[
                  { title: '❌ Using a Selfie', text: "Your beach vacation photo might be great for Instagram, but use a professional headshot for your business card. First impressions matter!" },
                  { title: '❌ Information Overload', text: "Don't list every social media account you've ever had. Keep it relevant – quality over quantity!" },
                  { title: '❌ Forgetting to Test', text: "Always test your links! Nothing's worse than a broken LinkedIn link when someone's trying to connect." }
                ].map((mistake, i) => (
                  <div key={i} className="bg-red-50 rounded-2xl p-6 border-l-4 border-red-500">
                    <h4 className="text-card-title text-dark mb-2">{mistake.title}</h4>
                    <p className="text-body text-gray-600">{mistake.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-primary-blue to-accent-purple relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center">
            <h2 className="text-h2 text-white mb-4">Ready to Create Your Card?</h2>
            <p className="text-body-lg text-white/90 mb-8 max-w-2xl mx-auto">
              You've got the knowledge, now it's time for action! In just 10 minutes, you'll have a professional digital business card that works as hard as you do.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/personal-card" className="btn bg-white text-primary-blue hover:bg-gray-100 transition-all shadow-lg">
                <FontAwesomeIcon icon="user" />
                <span>About Personal Card</span>
              </Link>
              <Link to="/business-card" className="btn bg-white/20 text-white hover:bg-white/30 transition-all">
                <FontAwesomeIcon icon="briefcase" />
                <span>About Business Card</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Author & Related */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="border-t pt-8 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-blue to-accent-purple flex items-center justify-center text-white font-bold text-body-lg flex-shrink-0">ST</div>
                <div>
                  <h3 className="text-card-title text-dark">Support Team</h3>
                  <p className="text-body-sm text-gray-500 mb-2">AM CARDMATE Customer Success</p>
                  <p className="text-body-sm text-gray-600">We're the friendly folks who help thousands of professionals set up their digital business cards every day. Our mission? Making sure your setup experience is smooth, simple, and maybe even fun!</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-h3 text-dark mb-6">Next Steps</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {[
                  { to: '/blog/creative-ways-use-nfc-card', title: '10 Creative Ways to Use Your NFC Card', gradient: 'from-pink-400 to-yellow-400' },
                  { to: '/blog/track-networking-roi', title: 'Track Your Networking ROI', gradient: 'from-teal-400 to-blue-500' },
                  { to: '/blog/how-nfc-business-cards-work', title: 'How NFC Business Cards Work', gradient: 'from-purple-400 to-pink-500' }
                ].map((post, i) => (
                  <Link key={i} to={post.to} className={`group ${i === 2 ? 'sm:col-span-2 md:col-span-1' : ''}`}>
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                      <div className={`h-40 bg-gradient-to-br ${post.gradient}`}></div>
                      <div className="p-4">
                        <h4 className="text-body font-bold text-dark group-hover:text-primary-blue transition-colors">{post.title}</h4>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SettingUpDigitalBusinessCard;